/**
 * What can raise an alarm at the org (GET /v1/twin/live-feed; platform-core/internal/twin/live_feed.go). The alarm
 * engine fires on readings that are not imported (history never alarms), so an empty Alarms page says what is true of
 * the org's own sources (the 2026-10-24 page sweep, H1; the #1641 review, I1):
 *   - "live": a gateway reported within 30 days (whatever its tags; a device under it reporting counts), or the org
 *     has a tag people enter readings into: the engine can fire, and the pages keep "All clear";
 *   - "quiet": the org's gateways reported once and none within 30 days: nothing can raise an alarm;
 *   - "files": no gateway that ever reported (a placeholder, or one just added, counts for nothing until its first
 *     report) and no hand entry, and data from files: everything is imported;
 *   - "none": none of these (no data yet), and while the answer is unknown: the pages keep their own words.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";

const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

export type AlarmFeedState = "live" | "quiet" | "files" | "none";

export interface AlarmFeed {
  state: AlarmFeedState;
  gateway_live: boolean;
  gateways: boolean;
  hand_entry: boolean;
  imports: boolean;
}

export const LIVE_FEED_KEY = ["twin-live-feed"] as const;

export const liveFeedApi = {
  get: () => api.get<AlarmFeed>("/v1/twin/live-feed"),
};

/** The answer, read once every five minutes at most (a source starts or stops rarely). */
export function useLiveFeedQuery() {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return useQuery({
    queryKey: [...LIVE_FEED_KEY, activeOrgId],
    queryFn: liveFeedApi.get,
    enabled: isLiveApiFlag && isAuthenticated && activeOrgId !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

const STATES: readonly AlarmFeedState[] = ["live", "quiet", "files", "none"];

/** The state the platform answered, or undefined while unknown or for an answer that does not read. Pure. */
export function alarmFeedStateOf(feed: Partial<AlarmFeed> | undefined): AlarmFeedState | undefined {
  const s = feed?.state;
  return s !== undefined && STATES.includes(s) ? s : undefined;
}

/** The org's state (alarmFeedStateOf over the read); undefined while unknown. */
export function useAlarmFeedState(): AlarmFeedState | undefined {
  return alarmFeedStateOf(useLiveFeedQuery().data);
}

/** What an empty alarm list says when nothing can raise an alarm: the org's gateways have gone quiet. */
export const QUIET_NO_ALARMS = "No alarms. The plant's gateways have sent nothing in the last 30 days, so nothing can raise one.";

/** What an empty alarm list says at a plant whose data all comes from files. */
export const FILE_FED_NO_ALARMS =
  "No alarms. This plant sends its data as files, so alarms are not raised from it. Limits still show on each machine.";

/**
 * The words an empty alarm list takes in this state, as a title and a description; null where the page keeps its own
 * ("live": the engine can fire; "none" and unknown: nothing to say). Pure.
 */
export function noAlarmsWords(state: AlarmFeedState | undefined): { title: string; description: string } | null {
  const text = state === "quiet" ? QUIET_NO_ALARMS : state === "files" ? FILE_FED_NO_ALARMS : null;
  if (!text) return null;
  return { title: "No alarms", description: text.replace(/^No alarms\. /, "") };
}
